import type {
  IPropertyConfiguration,
  ITemplatePropertyInput,
} from '~/client/graph/types.generated'
import { PropertyType } from '~/client/graph/types.generated'

export interface PropertyData {
  propertyId: string
  label: string
  name: string
  type: PropertyType
  configuration?: Partial<IPropertyConfiguration> | null
  required: boolean
  isHidden: boolean
  /**
   * Declared mandatory by the resource (form.mandatoryPropertyKeys): always included and
   * always required, so its selection checkbox is inert rather than absent - it stays
   * visibly ticked, like the system rows above it.
   */
  isLocked?: boolean
  orderIndex: number
}

/**
 * Settings the form-properties editor allows a user to change.
 */
export interface FormPropertyEdit {
  readonly propertyId: string
  readonly isRequired: boolean
  readonly isHidden: boolean
}

/**
 * Builds the complete template-property replacement payload.
 *
 * Edited properties keep their relative UI order, saved properties outside the
 * editor keep their absolute slots, and settings the editor does not own are
 * preserved. The resulting indexes are always unique and contiguous.
 */
export const buildTemplatePropertyInputs = (
  editedProperties: readonly FormPropertyEdit[],
  savedProperties: readonly ITemplatePropertyInput[] = [],
): ITemplatePropertyInput[] => {
  const savedById = new Map(
    savedProperties.map(property => [property.propertyId, property] as const),
  )
  const editedPropertyIds = new Set(
    editedProperties.map(property => property.propertyId),
  )
  const indexedSavedProperties = savedProperties
    .map((property, sourceIndex) => ({ property, sourceIndex }))
    .sort(
      (left, right) =>
        left.property.orderIndex - right.property.orderIndex ||
        left.sourceIndex - right.sourceIndex,
    )
  let editIndex = 0
  const mergedProperties = indexedSavedProperties.map(({ property }) => {
    if (!editedPropertyIds.has(property.propertyId)) return { ...property }

    const edit = editedProperties[editIndex++]
    if (!edit) return { ...property }

    const savedEdit = savedById.get(edit.propertyId)
    return {
      ...savedEdit,
      propertyId: edit.propertyId,
      orderIndex: 0,
      isRequired: edit.isRequired,
      isHidden: edit.isHidden,
      defaultValue: savedEdit?.defaultValue ?? null,
    }
  })

  const remainingEdits = editedProperties.slice(editIndex).map(property => ({
    propertyId: property.propertyId,
    orderIndex: 0,
    isRequired: property.isRequired,
    isHidden: property.isHidden,
    defaultValue: savedById.get(property.propertyId)?.defaultValue ?? null,
  }))

  return [...mergedProperties, ...remainingEdits].map(
    (property, orderIndex) => ({
      ...property,
      orderIndex,
    }),
  )
}

export const sortFormProperties = <
  T extends Pick<PropertyData, 'isHidden' | 'orderIndex'>,
>(
  list: readonly T[],
): T[] =>
  [...list].sort((a, b) => {
    if (a.isHidden === b.isHidden) {
      return a.orderIndex - b.orderIndex
    }
    return a.isHidden ? 1 : -1
  })
